import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2, CornerDownLeft, Plus, ArrowRight, LayoutGrid, Clock, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { buildCommands, SUGGESTED } from "../../pages/os/commandItems";

const REC_KEY = "omnia_cmd_recent";
const loadRecents = () => { try { return JSON.parse(localStorage.getItem(REC_KEY) || "[]"); } catch { return []; } };

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [recents, setRecents] = useState(loadRecents);
  const navigate = useNavigate();
  const inputRef = useRef();
  const listRef = useRef();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(true); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => { if (open) { setRecents(loadRecents()); setTimeout(() => inputRef.current?.focus(), 50); } else { setQ(""); setResults([]); setActive(0); } }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setResults(data.results || []);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const { navs, actions, apps } = useMemo(() => buildCommands(q), [q]);

  // Secciones ordenadas → lista plana para navegación con teclado.
  const sections = useMemo(() => {
    if (q.trim().length < 1) {
      const s = [];
      if (recents.length) s.push({ title: "Recientes", icon: Clock, items: recents });
      s.push({ title: "Sugeridos", icon: Sparkles, items: SUGGESTED });
      return s;
    }
    const s = [];
    if (actions.length) s.push({ title: "Acciones rápidas", icon: Plus, items: actions.map((a) => ({ ...a, label: a.label, sub: a.sub })) });
    if (navs.length || apps.length) s.push({ title: "Ir a", icon: ArrowRight, items: [...navs.map((n) => ({ label: n.label, link: n.link })), ...apps.map((a) => ({ label: a.label, link: a.link }))] });
    if (results.length) s.push({ title: "Resultados", icon: LayoutGrid, items: results.map((r) => ({ label: r.title, sub: r.type, link: r.link })) });
    return s;
  }, [q, actions, navs, apps, results, recents]);

  const flat = useMemo(() => sections.flatMap((s) => s.items.map((it) => ({ ...it, section: s.title }))), [sections]);
  useEffect(() => { setActive(0); }, [q, results.length, open]);

  const go = (it) => {
    if (!it?.link) return;
    const rec = [{ label: it.label, sub: it.sub, link: it.link }, ...loadRecents().filter((r) => r.link !== it.link)].slice(0, 6);
    localStorage.setItem(REC_KEY, JSON.stringify(rec));
    setOpen(false);
    navigate(it.link);
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (flat[active]) go(flat[active]); }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-cmd-index="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active]);

  let idx = -1;
  const showEmpty = q.trim().length >= 2 && flat.length === 0 && !loading;

  return (
    <>
      <button data-testid="global-search-btn" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-full border border-navy/15 text-navy/50 hover:text-navy hover:border-navy/30 text-sm pl-3 pr-2 py-2 transition-colors">
        <Search className="w-4 h-4" />
        <span className="hidden md:inline">Buscar</span>
        <kbd className="hidden md:inline text-[10px] bg-navy/5 rounded px-1.5 py-0.5">⌘K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center pt-24 px-4 bg-navy/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div data-testid="global-search-modal" onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-cream rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center gap-3 px-5 border-b border-navy/10">
              <Search className="w-5 h-5 text-navy/40" />
              <input ref={inputRef} data-testid="global-search-input" value={q}
                onChange={(e) => setQ(e.target.value)} onKeyDown={onInputKey}
                placeholder="Busca o ejecuta una acción — usa ↑ ↓ y Enter…"
                className="flex-1 bg-transparent py-4 outline-none text-navy placeholder:text-navy/35" />
              {loading && <Loader2 className="w-4 h-4 animate-spin text-navy/40" />}
              <button onClick={() => setOpen(false)} className="text-navy/40 hover:text-navy"><X className="w-4 h-4" /></button>
            </div>

            <div ref={listRef} className="max-h-96 overflow-y-auto pb-2">
              {showEmpty && <p data-testid="global-search-empty" className="px-5 py-6 text-sm text-navy/40">Sin resultados para "{q}".</p>}
              {sections.map((sec) => {
                const Icon = sec.icon;
                return (
                  <div key={sec.title} className="py-1.5">
                    <div className="px-5 py-1 text-[10px] uppercase tracking-wide text-navy/35 flex items-center gap-1.5"><Icon className="w-3 h-3" /> {sec.title}</div>
                    {sec.items.map((it) => {
                      idx += 1; const i = idx; const isActive = i === active;
                      return (
                        <button key={`${sec.title}-${it.link}-${i}`} data-cmd-index={i}
                          data-testid={`cmd-item-${i}`}
                          onMouseEnter={() => setActive(i)} onClick={() => go(it)}
                          className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors ${isActive ? "bg-violet/10" : "hover:bg-navy/5"}`}>
                          {sec.title === "Acciones rápidas" ? <Plus className="w-4 h-4 text-violet shrink-0" />
                            : sec.title === "Resultados" ? <span className="text-[10px] uppercase tracking-wide text-violet bg-violet/10 rounded px-2 py-0.5 shrink-0">{it.sub}</span>
                            : <ArrowRight className="w-4 h-4 text-navy/40 shrink-0" />}
                          <span className="flex-1 min-w-0">
                            <span className="text-sm text-navy truncate block">{it.label}</span>
                            {it.sub && sec.title !== "Resultados" && <span className="text-[11px] text-navy/40 truncate block">{it.sub}</span>}
                          </span>
                          {isActive && <CornerDownLeft className="w-3.5 h-3.5 text-navy/30 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
